import type { Ref } from "react";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { mergeRefs } from "react-merge-refs";
import clsx from "clsx";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";

import { BaseInput } from "../baseInput/baseInput";
import rootClasses from "../styles.module.scss";
import { HintText } from "../hintText";
import { InputIcon } from "../inputIcon";

import classes from "./styles.module.scss";
import type { IDropdownMultiSelectProps } from "./types";

import { Icon } from "@/components/atoms/icon";
import { Box } from "@/components/atoms/box";
import { DropdownItem } from "@/components/atoms/dropdownItem/dropdownItem";
import { DropdownSearch } from "@/components/atoms/dropdownSearch/dropdownSearch";
import { Typography } from "@/components/atoms/typography";
import { ButtonLoader } from "@components/atoms/loader/buttonLoader";
import { FastLinkButton } from "@components/fastFields/fastLinkButton";
import type { ID } from "@/interfaces";

export const DropdownMultiSelect = forwardRef(function Dropdown<T = unknown>(
  props: IDropdownMultiSelectProps<T>,
  ref: Ref<HTMLInputElement>,
) {
  const {
    onSearchChange,
    options,
    value = [],
    onKeyDown: propsOnKeyDown,
    onValueChange,
    disabled,
    readOnly,
    isInvalid,
    inputSize = "medium",
    noSearch,
    hintText,
    iconName,
    isFetchingNextPage,
    isLoading,
    isError,
    onGetNext,
    renderItem: _renderItem,
    rootClassName,
    superRootClassName,
    noClear,
    ...rest
  } = props;

  const { t } = useTranslation("component");

  const inputRef = useRef<HTMLInputElement>(null);

  const [open, rawSetOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const isSelected = (key: ID) => value.some((item) => item.key === key);

  const setOpen = useCallback(
    (value: boolean) => {
      if (!readOnly && !disabled) {
        if (value) {
          setSearchInputValue("");
        }
        rawSetOpen(value);
      }
    },
    [disabled, readOnly],
  );

  const { ref: loadMoreContainerRef, inView } = useInView();

  useEffect(() => {
    if (inView) {
      onGetNext?.();
    }
    // !important do not remove this comment
    // and no not add onGetNext to deps otherwise it cause loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const [searchInputValue, setSearchInputValue] = useState("");

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            MinWidth: `300px`,
          });
        },
      }),
    ],
  });

  const listRef = useRef<(HTMLElement | null)[]>([]);

  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: "listbox",
  });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: false,
  });

  const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions(
    [dismiss, role, listNav],
  );

  const handleSelect = () => {
    if (activeIndex === null) return;

    const activeItem = options[activeIndex];

    if (activeItem) {
      if (isSelected(activeItem?.key)) {
        onValueChange?.(value?.filter((item) => item?.key !== activeItem?.key));
      } else {
        onValueChange?.([...value, activeItem]);
      }
    } else throw new Error("activeItem is undefined");
    setActiveIndex(null);
    setOpen(false);
  };

  const clearValue = () => {
    onValueChange?.([]);
  };

  return (
    <HintText
      disabled={disabled}
      isInvalid={isInvalid}
      text={hintText}
      className={superRootClassName}
    >
      <div
        className={clsx([
          rootClasses.inputBox,
          classes.dropdownInputBox,
          rootClassName,
        ])}
        data-isinvalid={isInvalid}
        data-size={inputSize}
        data-read-only={readOnly}
        data-disabled={disabled}
        ref={refs.setReference}
        {...getReferenceProps({
          onMouseDown: (event) => {
            event.preventDefault();
            inputRef.current?.focus();
          },
          onClick: () => {
            setOpen(!open);
          },
        })}
      >
        {iconName && (
          <Icon
            name={iconName}
            className={clsx("icon", classes.flex0)}
            fontSize={"fs-20"}
          />
        )}
        <BaseInput
          readOnly
          disabled={disabled}
          value={value?.map((item) => item?.value)?.join(", ")}
          ref={mergeRefs([inputRef, ref])}
          onKeyDown={(event) => {
            if (
              !readOnly &&
              !disabled &&
              event.key === "Backspace" &&
              noClear !== true
            ) {
              clearValue();
            }

            propsOnKeyDown?.(event);
          }}
          {...rest}
        />
        {value.length > 0 && !disabled && !readOnly && noClear !== true ? (
          <InputIcon
            tabIndex={-1}
            onClick={() => {
              clearValue();
            }}
          />
        ) : (
          <Icon
            className={clsx("icon", classes.flex0)}
            name={"ArrowDownSLine"}
          />
        )}
      </div>
      <FloatingPortal>
        {open && (
          <FloatingFocusManager
            context={context}
            initialFocus={0}
            visuallyHiddenDismiss
          >
            <Box
              {...getFloatingProps({
                ref: refs.setFloating,
                style: {
                  ...floatingStyles,
                  zIndex: "100",
                },
                onKeyDown: (event) => {
                  if (
                    event.key === "Enter" &&
                    activeIndex != null &&
                    options[activeIndex]
                  ) {
                    event.preventDefault();
                    handleSelect();
                  }
                },
              })}
              gridGap={"8"}
              py={"6"}
            >
              {onSearchChange && noSearch !== true && (
                <DropdownSearch
                  value={searchInputValue}
                  onValueChange={setSearchInputValue}
                  containerClassName={"mx-6"}
                  onDebouncedChange={onSearchChange}
                />
              )}
              <Box
                px={"6"}
                gridGap={"4"}
                noBorder
                style={{ maxHeight: "300px" }}
              >
                {options.length === 0 && !isLoading && !isFetchingNextPage && (
                  <Typography
                    themeColor={"text-soft-400"}
                    fontType={"label-x-small"}
                    textCenter
                  >
                    {t("common.noData")}
                  </Typography>
                )}
                {options.map((item, index) => (
                  <DropdownItem
                    key={item.key}
                    {...getItemProps({
                      ref(node) {
                        listRef.current[index] = node;
                      },
                      onClick: handleSelect,
                    })}
                    isSelected={isSelected(item?.key)}
                    text={item.value}
                    description={item.description}
                    iconName={item.icon}
                  />
                ))}
                {(isFetchingNextPage || isLoading) && (
                  <div
                    className={
                      "w-100 d-flex justify-content-center align-items-center"
                    }
                    style={{ height: "2rem" }}
                  >
                    <ButtonLoader />
                  </div>
                )}
                {!!onGetNext && (
                  <div
                    className={"w-100 d-flex justify-content-center"}
                    ref={loadMoreContainerRef}
                  >
                    <FastLinkButton
                      type={"loadMore"}
                      onClick={() => {
                        onGetNext?.();
                      }}
                    />
                  </div>
                )}
              </Box>
            </Box>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </HintText>
  );
});
